import { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { captureWithMessage } from '@/lib/captureWithMessage';
import { Plus, Trash2, Flame, Camera, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cachedQuery, offlineMutation } from '@/lib/offlineStorage';
import { useReconnectRefetch } from '@/hooks/useReconnectRefetch';
import { ResilientImage } from '@/components/ResilientImage';
import { toast } from 'sonner';
import { getTodayLocalString } from '@/lib/dateUtils';
import { CARDIO_EQUIPMENT, getEquipmentLabel } from '@/lib/cardioEquipment';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ExerciseLogCardProps {
  activeUser: string;
  userId?: string;
  refreshTrigger?: number;
  onLogged?: () => void;
  onCardioKcalChange?: (kcal: number) => void;
}

interface PendingLog {
  exType: string;
  exDesc: string;
  exCals: string;
  exEquipment: string;
  exDuration: string;
  exPhotoUrl: string | null;
  exNotes: string;
}

interface ExerciseLogRow {
  id: number;
  exercise_type: string;
  description: string | null;
  calories_burned: number;
  log_date: string;
  equipment?: string | null;
  duration_minutes?: number | null;
  evidence_photo_url?: string | null;
  notes?: string | null;
}

export function ExerciseLogCard({ activeUser, userId, refreshTrigger, onLogged, onCardioKcalChange }: ExerciseLogCardProps) {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [exType, setExType] = useState('gym');
  const [exDesc, setExDesc] = useState('');
  const [exCals, setExCals] = useState('');
  const [exEquipment, setExEquipment] = useState<string>('caminadora');
  const [exDuration, setExDuration] = useState<string>('');
  const [exPhotoFile, setExPhotoFile] = useState<File | null>(null);
  const [exPhotoUrl, setExPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [exNotes, setExNotes] = useState('');
  const [pendingLog, setPendingLog] = useState<PendingLog | null>(null);
  const [routineCardioLogs, setRoutineCardioLogs] = useState<ExerciseLogRow[]>([]);
  const [exerciseLogs, setExerciseLogs] = useState<ExerciseLogRow[]>([]);
  const [exerciseToDelete, setExerciseToDelete] = useState<ExerciseLogRow | null>(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const today = getTodayLocalString();
  const targetUserId = userId || user?.id;

  // Logs de cardio del día — vía cachedQuery para que offline siga
  // mostrando el cardio que el cliente registró hoy y no se pierda
  // la card en blanco esperando red.
  const fetchTodayLogs = () => {
    if (!targetUserId) return;
    cachedQuery<ExerciseLogRow[]>(
      `exercise_logs_${targetUserId}_${activeUser}_${today}`,
      () => supabase
        .from('exercise_logs')
        .select('id, exercise_type, description, calories_burned, log_date, equipment, duration_minutes, evidence_photo_url, notes')
        .eq('user_id', targetUserId)
        .eq('profile_name', activeUser)
        .eq('log_date', today)
        .order('id', { ascending: false })
    ).then(({ data }) => {
      if (data) setExerciseLogs(data as ExerciseLogRow[]);
    });
  };

  useEffect(() => { fetchTodayLogs(); }, [targetUserId, activeUser, refreshTrigger]);
  // Refetch cuando vuelve la red — reemplaza el cache viejo con datos
  // frescos sin que el cliente tenga que recargar la app.
  useReconnectRefetch(fetchTodayLogs);

  useEffect(() => {
    const total = exerciseLogs
      .filter(e => e.exercise_type === 'cardio')
      .reduce((s, e) => s + Number(e.calories_burned), 0);
    onCardioKcalChange?.(total);
  }, [exerciseLogs]);

  if (!user) return null;

  const doInsert = async (data: PendingLog) => {
    // Para cardio: la descripción puede venir vacía si solo escogió aparato.
    // Construimos una desc "humana" combinando aparato + duración si aplica,
    // así el coach ve "Caminadora · 35 min" en su panel.
    let finalDesc = data.exDesc || null;
    if (data.exType === 'cardio') {
      const equipLabel = data.exEquipment && data.exEquipment !== 'otro'
        ? CARDIO_EQUIPMENT.find(e => e.id === data.exEquipment)?.label
        : data.exDesc;
      const durPart = data.exDuration ? ` · ${data.exDuration} min` : '';
      finalDesc = equipLabel ? `${equipLabel}${durPart}` : data.exDesc || null;
    }

    const insertData: Record<string, unknown> = {
      user_id: user.id,
      profile_name: activeUser,
      exercise_type: data.exType,
      description: finalDesc,
      calories_burned: Number(data.exCals),
      log_date: today,
    };
    if (data.exType === 'cardio') {
      insertData.equipment = data.exEquipment || null;
      if (data.exDuration) insertData.duration_minutes = Number(data.exDuration);
      if (data.exPhotoUrl) insertData.evidence_photo_url = data.exPhotoUrl;
    }
    if (data.exNotes) insertData.notes = data.exNotes;

    const tempLog: ExerciseLogRow = {
      id: -Date.now(),
      exercise_type: data.exType,
      description: finalDesc,
      calories_burned: Number(data.exCals),
      log_date: today,
      equipment: data.exType === 'cardio' ? data.exEquipment : null,
      duration_minutes: data.exDuration ? Number(data.exDuration) : null,
      evidence_photo_url: data.exPhotoUrl,
      notes: data.exNotes || null,
    };
    setExerciseLogs(prev => [tempLog, ...prev]);
    await offlineMutation('exercise_logs', 'insert', insertData, () =>
      supabase.from('exercise_logs').insert(insertData)
    );
    toast.success('Ejercicio registrado');
    onLogged?.();
  };

  const deleteExercise = async (id: number) => {
    setExerciseLogs(prev => prev.filter(e => e.id !== id));
    await offlineMutation('exercise_logs', 'delete', { id }, () =>
      supabase.from('exercise_logs').delete().eq('id', id),
      ['id']
    );
    onLogged?.();
  };

  const resetForm = () => {
    setExDesc('');
    setExCals('');
    setExEquipment('caminadora');
    setExDuration('');
    setExPhotoFile(null);
    setExPhotoUrl(null);
    setExNotes('');
    setShowForm(false);
    setPendingLog(null);
    setRoutineCardioLogs([]);
  };

  const handlePhotoSelect = async (file: File) => {
    if (!user) return;
    setUploadingPhoto(true);
    setExPhotoFile(file);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `${user.id}/${today}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('cardio-evidence')
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw error;
      setExPhotoUrl(path);
      toast.success('📷 Foto adjuntada');
    } catch (err) {
      console.error('Error subiendo foto:', err);
      toast.error('No se pudo subir la foto');
      setExPhotoFile(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const buildPayload = (): PendingLog => ({
      exType, exDesc, exCals, exEquipment, exDuration,
      exPhotoUrl, exNotes,
    });

    if (exType === 'cardio') {
      const { data } = await supabase
        .from('exercise_logs')
        .select('id, exercise_type, description, calories_burned, log_date')
        .eq('user_id', user.id)
        .eq('exercise_type', 'cardio')
        .eq('log_date', today);

      const rutineLogs = (data || []).filter(
        l => typeof l.description === 'string' && l.description.startsWith('[RUTINA]')
      );

      if (rutineLogs.length > 0) {
        setPendingLog(buildPayload());
        setRoutineCardioLogs(rutineLogs as ExerciseLogRow[]);
        return;
      }
    }

    await doInsert(buildPayload());
    resetForm();
  };

  const handleSameExercise = () => {
    toast.info('Se usó el registro de tu rutina, sin duplicar');
    resetForm();
  };

  const handleDifferentExercise = async () => {
    if (!pendingLog) return;
    await doInsert(pendingLog);
    resetForm();
  };

  const displayDescription = (desc: string | null) =>
    desc?.replace('[RUTINA] ', '') ?? '—';

  const handleWhatsAppEvidence = async () => {
    // Resumen del día para el mensaje
    const cardioLogs = exerciseLogs.filter(e => e.exercise_type === 'cardio');
    const totalCardioKcal = cardioLogs.reduce((s, e) => s + Number(e.calories_burned), 0);
    const fecha = new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

    if (cardioLogs.length === 0) {
      toast.warning('Registra al menos un cardio antes de enviar evidencia');
      return;
    }

    const detalles = cardioLogs
      .map(l => `  • ${displayDescription(l.description)} — ${l.calories_burned} kcal`)
      .join('\n');
    const message = `🔥 ¡Completé mi cardio!\n\n📅 Fecha: ${fecha}\n🏃 Total quemado: ${totalCardioKcal} kcal\n📋 Sesiones:\n${detalles}\n👤 Cliente: ${activeUser}`;
    const whatsappUrl = 'https://chat.whatsapp.com/JHO3fHk50OdL3dB84G7sgE?mode=gi_t';

    // 1) Capturar el resumen CON el texto del mensaje embebido abajo,
    //    así toda la info viaja en un solo PNG aunque WhatsApp descarte
    //    el caption del share (común en iOS).
    let imageBlob: Blob | null = null;
    if (summaryRef.current) {
      try {
        imageBlob = await captureWithMessage(summaryRef.current, message);
      } catch (err) {
        console.error('Error al capturar screenshot de cardio:', err);
      }
    }

    // 2) Web Share API con la imagen (que ya contiene el texto embebido)
    if (imageBlob && typeof navigator.share === 'function') {
      const file = new File([imageBlob], `cardio-${today}.png`, { type: 'image/png' });
      try {
        const canShareFiles = typeof (navigator as any).canShare === 'function'
          ? (navigator as any).canShare({ files: [file] })
          : true;
        if (canShareFiles) {
          // Solo `files` — el mensaje ya está dentro de la imagen.
          await navigator.share({ files: [file] });
          return;
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }

    // 3) Web Share solo texto (sin imagen)
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ text: message });
        return;
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
      }
    }

    // 4) Último recurso: copiar texto al portapapeles + abrir grupo de WhatsApp
    try {
      await navigator.clipboard.writeText(message);
      toast.success('📋 Mensaje copiado — pégalo en el grupo', { duration: 5000 });
    } catch {
      toast.info('Abriendo WhatsApp…');
    }
    setTimeout(() => { window.location.href = whatsappUrl; }, 600);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={() => setShowForm(v => !v)}
          className="fitness-btn-primary flex items-center gap-2 justify-center min-h-[48px] flex-1"
        >
          <Plus className="w-4 h-4" /> Registrar Ejercicio
        </button>
        <button
          onClick={handleWhatsAppEvidence}
          className="flex items-center gap-2 justify-center min-h-[48px] px-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all bg-[#25D366] hover:bg-[#1ebe5b] text-white shadow-sm sm:w-auto w-full"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current shrink-0" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Envía evidencia por WhatsApp
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-card border-4 border-secondary p-4 md:p-6 mt-3 rounded-xl space-y-4"
        >
          {/* Toggle Tipo: 2 botones grandes para móvil */}
          <div>
            <label className="fitness-label block mb-2">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setExType('cardio')}
                className={`min-h-[48px] rounded-xl font-black uppercase text-xs tracking-widest border-2 transition-all ${
                  exType === 'cardio'
                    ? 'bg-accent text-accent-foreground border-accent'
                    : 'bg-card text-muted-foreground border-secondary hover:border-accent/40'
                }`}
              >
                🔥 Cardio
              </button>
              <button
                type="button"
                onClick={() => setExType('gym')}
                className={`min-h-[48px] rounded-xl font-black uppercase text-xs tracking-widest border-2 transition-all ${
                  exType === 'gym'
                    ? 'bg-accent text-accent-foreground border-accent'
                    : 'bg-card text-muted-foreground border-secondary hover:border-accent/40'
                }`}
              >
                💪 Gym
              </button>
            </div>
          </div>

          {/* Campos específicos según tipo */}
          {exType === 'cardio' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="fitness-label block mb-2">Aparato</label>
                  <select
                    value={exEquipment}
                    onChange={e => setExEquipment(e.target.value)}
                    className="fitness-input"
                  >
                    {CARDIO_EQUIPMENT.map(eq => (
                      <option key={eq.id} value={eq.id}>{eq.emoji} {eq.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="fitness-label block mb-2">
                    Duración <span className="text-muted-foreground font-normal normal-case">(min — opcional)</span>
                  </label>
                  <input
                    type="number"
                    value={exDuration}
                    onChange={e => setExDuration(e.target.value)}
                    className="fitness-input"
                    min="0"
                    placeholder="35"
                  />
                </div>
              </div>

              {exEquipment === 'otro' && (
                <div>
                  <label className="fitness-label block mb-2">¿Qué aparato?</label>
                  <input
                    value={exDesc}
                    onChange={e => setExDesc(e.target.value)}
                    className="fitness-input"
                    placeholder="Ej: Stepper, Battle ropes…"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="fitness-label block mb-2">Calorías quemadas</label>
                  <input
                    type="number"
                    value={exCals}
                    onChange={e => setExCals(e.target.value)}
                    className="fitness-input"
                    required
                    min="0"
                    placeholder="540"
                  />
                </div>
                <div>
                  <label className="fitness-label block mb-2">
                    Foto del display <span className="text-muted-foreground font-normal normal-case">(opcional)</span>
                  </label>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) handlePhotoSelect(f);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="w-full min-h-[44px] rounded-xl border-2 border-dashed border-secondary hover:border-accent/40 transition-colors flex items-center justify-center gap-2 text-xs font-bold text-muted-foreground disabled:opacity-50"
                  >
                    <Camera className="w-4 h-4" />
                    {uploadingPhoto ? 'Subiendo…' : exPhotoFile ? `✓ ${exPhotoFile.name.slice(0, 20)}…` : 'Adjuntar foto'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="fitness-label block mb-2">Calorías quemadas</label>
                <input
                  type="number"
                  value={exCals}
                  onChange={e => setExCals(e.target.value)}
                  className="fitness-input"
                  required
                  min="0"
                  placeholder="320"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Las kcal que tu reloj o Apple Watch midió durante el entrenamiento
                </p>
              </div>
              <div>
                <label className="fitness-label block mb-2">
                  Nota <span className="text-muted-foreground font-normal normal-case">(opcional)</span>
                </label>
                <input
                  value={exNotes}
                  onChange={e => setExNotes(e.target.value)}
                  className="fitness-input"
                  placeholder="Ej: Pierna pesado, Push, Pull…"
                />
              </div>
            </>
          )}

          <button type="submit" className="fitness-btn-primary w-full min-h-[48px]">
            Guardar
          </button>
        </form>
      )}

      {exerciseLogs.length > 0 && (
        <div ref={summaryRef} className="mt-3 space-y-2 bg-card border-2 border-secondary rounded-xl p-3">
          {/* Header del resumen — visible al usuario y capturado en el screenshot */}
          <div className="flex items-center justify-between gap-2 pb-2 border-b border-secondary/60">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-accent" />
              <span className="text-xs font-black uppercase tracking-wider">Resumen de hoy</span>
            </div>
            <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">
              {new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
              {' · '}{activeUser}
            </div>
          </div>

          {(() => {
            const cardioLogs = exerciseLogs.filter(e => e.exercise_type === 'cardio');
            const gymLogs   = exerciseLogs.filter(e => e.exercise_type !== 'cardio');
            const cardioKcal = cardioLogs.reduce((s, e) => s + Number(e.calories_burned), 0);
            const gymKcal   = gymLogs.reduce((s, e) => s + Number(e.calories_burned), 0);
            return (
              <div className="grid grid-cols-2 gap-2 pb-1">
                <div className="bg-secondary/40 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Cardio</div>
                  <div className="text-base font-black text-accent">{cardioKcal} <span className="text-[10px] font-bold text-muted-foreground">kcal</span></div>
                  <div className="text-[10px] text-muted-foreground">{cardioLogs.length} sesión{cardioLogs.length !== 1 ? 'es' : ''}</div>
                </div>
                <div className="bg-secondary/40 rounded-lg p-2 text-center">
                  <div className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Gym</div>
                  <div className="text-base font-black text-accent">{gymKcal} <span className="text-[10px] font-bold text-muted-foreground">kcal</span></div>
                  <div className="text-[10px] text-muted-foreground">{gymLogs.length} sesión{gymLogs.length !== 1 ? 'es' : ''}</div>
                </div>
              </div>
            );
          })()}

          {exerciseLogs.map(e => (
            <div key={e.id} className="bg-card border-2 border-secondary rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{displayDescription(e.description)}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="fitness-tag-active">{e.exercise_type}</span>
                  {e.duration_minutes && (
                    <span className="text-[10px] font-bold text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {e.duration_minutes} min
                    </span>
                  )}
                  {e.notes && (
                    <span className="text-[10px] text-muted-foreground italic truncate max-w-[120px]">
                      {e.notes}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.evidence_photo_url && (
                  <button
                    type="button"
                    onClick={() => setPhotoPreviewOpen(e.evidence_photo_url!)}
                    className="text-accent hover:opacity-80 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg"
                    aria-label="Ver foto"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                )}
                <span className="font-black text-green-500 text-sm">-{Number(e.calories_burned)}</span>
                <button
                  onClick={() => setExerciseToDelete(e)}
                  aria-label="Eliminar ejercicio"
                  className="text-muted-foreground hover:text-destructive transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog
        open={!!pendingLog}
        onOpenChange={open => { if (!open) { setPendingLog(null); setRoutineCardioLogs([]); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Es el mismo cardio de tu rutina?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Ya tienes cardio de rutina registrado hoy:</p>
                <ul className="space-y-1 pl-2 border-l-2 border-accent/40">
                  {routineCardioLogs.map(l => (
                    <li key={l.id} className="text-sm font-bold text-foreground">
                      {displayDescription(l.description)} — {l.calories_burned} kcal
                    </li>
                  ))}
                </ul>
                <p>¿El ejercicio que estás registrando es el mismo? Si coincide, lo vincularemos para no contar dos veces.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSameExercise}>
              Sí, es el mismo — vincular
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDifferentExercise}>
              No, es diferente — registrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview de foto de evidencia */}
      {photoPreviewOpen && (
        <PhotoPreview
          path={photoPreviewOpen}
          onClose={() => setPhotoPreviewOpen(null)}
        />
      )}

      {/* Confirm delete dialog — destructive actions need explicit confirmation */}
      <AlertDialog
        open={exerciseToDelete !== null}
        onOpenChange={(open) => { if (!open) setExerciseToDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este ejercicio?</AlertDialogTitle>
            <AlertDialogDescription>
              {exerciseToDelete && (
                <>
                  Vas a borrar <span className="font-bold text-foreground">
                    {exerciseToDelete.description || exerciseToDelete.exercise_type}
                  </span> ({exerciseToDelete.calories_burned} kcal). Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (exerciseToDelete) deleteExercise(exerciseToDelete.id);
                setExerciseToDelete(null);
              }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Modal para previsualizar la foto de evidencia. Genera URL firmada
 * (bucket privado) on-demand, así no exponemos URLs persistentes.
 */
function PhotoPreview({ path, onClose }: { path: string; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase.storage
      .from('cardio-evidence')
      .createSignedUrl(path, 60 * 10)
      .then(({ data }) => {
        if (cancelled) return;
        setUrl(data?.signedUrl || null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [path]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-2xl max-h-[90vh] w-full" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 bg-card border-2 border-border rounded-full w-10 h-10 flex items-center justify-center font-black text-lg"
          aria-label="Cerrar"
        >×</button>
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white">Cargando…</div>
        ) : url ? (
          <ResilientImage
            src={url}
            alt="Evidencia"
            className="w-full h-auto rounded-xl"
            fallback={
              <div className="text-white p-8 text-center bg-white/5 rounded-xl">
                No se pudo cargar la foto
              </div>
            }
          />
        ) : (
          <div className="text-white p-8 text-center">No se pudo cargar la foto</div>
        )}
      </div>
    </div>
  );
}
